import { Map } from "schemas/functions";
import { LIGHT_GREY } from "styles/oneOffColor";

const styles = {
  // Shared step layout
  stepSubtitle: { mb: 3, color: "text.secondary" },
  cardsGrid: { mb: 2 },
  actionsRowEnd: { display: "flex", justifyContent: "flex-end", mt: 2 },
  actionsRowSplit: { display: "flex", justifyContent: "space-between", mt: 4 },

  // Step1: Outreach card (taller hero)
  outreachCard: (option: Map, active: boolean) => ({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    p: { xs: "12px", sm: "16px" },
    gap: { xs: "10px", sm: "16px" },
    width: "100%",
    height: { xs: 140, sm: 160 },
    background: option.background,
    border: option.border ?? "1px solid transparent",
    borderRadius: "16px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    outline: active ? "2px solid #004537" : "2px solid transparent",
    outlineOffset: 2,
    boxShadow: active
      ? "0 8px 24px rgba(0, 69, 55, 0.18)"
      : "0 2px 8px rgba(21, 31, 22, 0.06)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 28px rgba(21, 31, 22, 0.12)",
    },
  }),
  iconWrapper: (option: Map) => ({
    width: { xs: 36, sm: 46 },
    height: { xs: 36, sm: 46 },
    borderRadius: "50%",
    backgroundColor: option.iconBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  icon: (option: Map) => ({
    fontSize: { xs: 14, sm: 18 },
    color: option.iconColor,
  }),
  cardLabel: (option: Map) => ({
    fontSize: { xs: 18, sm: 26 },
    fontWeight: 700,
    lineHeight: 1.15,
    color: option.textColor,
    letterSpacing: "-0.02em",
  }),
  cardSub: (option: Map) => ({
    mt: { xs: 0.5, sm: 1 },
    fontSize: { xs: 12, sm: 16 },
    color: option.subColor,
  }),
  cardCheck: (option: Map, active: boolean) => ({
    position: "absolute",
    top: { xs: 12, sm: 20 },
    right: { xs: 12, sm: 20 },
    fontSize: { xs: 20, sm: 26 },
    color: option.checkColor ?? "#004537",
    opacity: active ? 1 : 0.4,
    transition: "opacity 0.15s ease",
  }),

  // Step1 specific
  nameField: { mb: 4 },
  templateTypeLabel: { mt: 2 },

  // Step2: Method card (slim)
  methodCard: (option: Map, active: boolean) => ({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    p: { xs: "14px", sm: "18px" },
    gap: { xs: "12px", sm: "16px" },
    width: "100%",
    height: { xs: 128, sm: 170 },
    background: option.background,
    border: option.border ?? "1px solid transparent",
    borderRadius: "16px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    outline: active ? "2px solid #004537" : "2px solid transparent",
    outlineOffset: 2,
    boxShadow: active
      ? "0 8px 24px rgba(0, 69, 55, 0.18)"
      : "0 2px 8px rgba(21, 31, 22, 0.06)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 28px rgba(21, 31, 22, 0.12)",
    },
  }),
  methodIconWrapper: (option: Map) => ({
    width: { xs: 34, sm: 44 },
    height: { xs: 34, sm: 44 },
    borderRadius: "50%",
    backgroundColor: option.iconBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  methodIcon: (option: Map) => ({
    fontSize: { xs: 13, sm: 18 },
    color: option.iconColor,
  }),
  methodCardLabel: (option: Map) => ({
    fontSize: { xs: 17, sm: 24 },
    fontWeight: 700,
    lineHeight: 1.15,
    color: option.textColor,
    letterSpacing: "-0.01em",
  }),
  methodCardSub: (option: Map) => ({
    mt: 0.25,
    fontSize: { xs: 11, sm: 13 },
    color: option.subColor,
  }),
  methodCardCheck: (option: Map, active: boolean) => ({
    position: "absolute",
    top: { xs: 10, sm: 14 },
    right: { xs: 10, sm: 14 },
    fontSize: { xs: 16, sm: 22 },
    color: option.checkColor ?? "#004537",
    opacity: active ? 1 : 0.4,
    transition: "opacity 0.15s ease",
  }),
  methodCardContent: { display: "flex", flexDirection: "column" },

  // Step2 sections
  sectionTitle: { fontWeight: 600, mb: 0.25 },
  sectionSubtitle: { mb: 1.5 },
  checklistContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    mb: 3,
  },
  orPasteSeparator: { my: 1.5, textAlign: "center" as const },
  connectionChip: (connected: boolean) => ({
    backgroundColor: connected ? "primary.main" : "red.main",
    color: connected ? "#bbf7d0" : "error.main",
    fontWeight: 600,
    fontSize: 14,
    "& .MuiChip-icon": {
      mr: 0,
      color: connected ? "#bbf7d0" : "error.main",
    },
    px: 1,
  }),
  connectionChipIcon: { ml: "6px !important" },
  connectionRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap" as const,
    gap: 1.5,
    mb: 2,
  },
  connectionHelperText: {
    fontSize: 12,
    lineHeight: 1.4,
  },
  compactGoogleConnectButton: {
    paddingLeft: { xs: 1.5, md: 2 },
    paddingRight: { xs: 1.5, md: 2 },
    py: 0.5,
    minHeight: "32px",
    fontSize: 13,
  },
  offerTitle: { fontWeight: 600, mt: 3, mb: 1 },
  blankEditorCard: {
    p: 2.5,
    borderRadius: 3,
    backgroundColor: "white",
    border: `2px solid ${LIGHT_GREY}`,
    mt: 1,
  },

  // ContextChecklistItem
  checklistItem: (checked: boolean, highlighted = false) => ({
    border: `1.5px solid ${
      highlighted ? "#004537" : checked ? "#004537" : LIGHT_GREY
    }`,
    borderRadius: 3,
    p: 2,
    backgroundColor: "white",
    boxShadow: highlighted ? "0 4px 16px rgba(0, 69, 55, 0.10)" : "none",
    transition: "all 0.15s ease",
    position: "relative" as const,
  }),
  checklistItemHeader: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  },
  revealLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    mt: 1.5,
    color: "#7C3AED",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    fontFamily: "inherit",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    "&:hover": {
      color: "#6D28D9",
    },
  },
  checklistItemHeaderRight: {
    display: "flex",
    alignItems: "center",
    cursor: "default",
  },
  checklistItemBody: { mt: 1.5 },
  sourceCard: (active: boolean) => ({
    display: "flex",
    flexDirection: "column" as const,
    p: { xs: 1, sm: 1.25 },
    width: "100%",
    background: "#FFFFFF",
    border: `1.5px solid ${active ? "#004537" : LIGHT_GREY}`,
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.15s ease",
  }),
  sourceCardHeader: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: 1,
  },
  sourceCardBody: {
    pt: 1.5,
  },
  bestWorkItem: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 1,
    width: "100%",
  },
  bestWorkSelectCard: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: 1,
    px: { xs: 1.25, sm: 1.5 },
    py: 0.5,
    border: `1.5px solid ${LIGHT_GREY}`,
    borderRadius: "10px",
    backgroundColor: "white",
  },
  bestWorkContentCard: {
    p: { xs: 1.25, sm: 1.5 },
    border: `1.5px solid ${LIGHT_GREY}`,
    borderRadius: "10px",
    backgroundColor: "white",
  },
  bestWorkSelect: {
    flex: 1,
    minWidth: 0,
    fontSize: { xs: 13, sm: 14 },
    color: "#0A0A0A",
    "& .MuiSelect-select": {
      py: 0.5,
      pr: "24px !important",
    },
  },
  bestWorkOption: {
    display: "flex",
    flexDirection: "column" as const,
    py: 0.25,
  },
  bestWorkOptionLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#0A0A0A",
    lineHeight: 1.2,
  },
  bestWorkOptionSub: {
    fontSize: 12,
    color: "#6B7168",
    lineHeight: 1.3,
  },
  tonePresetChip: (active: boolean) => ({
    px: 1.5,
    py: 0.75,
    borderRadius: 2,
    border: `1.5px solid ${active ? "#004537" : LIGHT_GREY}`,
    backgroundColor: active ? "#004537" : "white",
    color: active ? "white" : "#0A0A0A",
    cursor: "pointer",
    display: "inline-flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: 0.25,
    fontFamily: "inherit",
    textAlign: "left" as const,
    transition: "all 0.15s ease",
    maxWidth: 260,
    "&:hover": { borderColor: "#004537" },
  }),
  tonePresetLabel: (active: boolean) => ({
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.2,
    color: active ? "white" : "#0A0A0A",
  }),
  tonePresetDescription: (active: boolean) => ({
    fontSize: 11,
    lineHeight: 1.3,
    color: active ? "rgba(255,255,255,0.85)" : "#6B7168",
  }),
  tonePresetEmoji: {
    mr: 0.75,
    fontSize: 14,
    lineHeight: 1,
  },
  tonePresetExampleLabel: (active: boolean) => ({
    mt: 0.5,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    color: active ? "rgba(255,255,255,0.7)" : "#9CA09A",
  }),
  tonePresetExample: (active: boolean) => ({
    fontSize: 11,
    lineHeight: 1.4,
    fontStyle: "italic" as const,
    color: active ? "rgba(255,255,255,0.85)" : "#6B7168",
  }),
  bestWorkAddButton: {
    alignSelf: "flex-start" as const,
    color: "#004537",
    borderColor: "#004537",
    "&:hover": {
      borderColor: "#004537",
      backgroundColor: "rgba(0, 69, 55, 0.04)",
    },
  },
  bestWorkRecommendedBadge: {
    display: "inline-flex",
    alignItems: "center",
    px: 0.875,
    height: 18,
    borderRadius: 999,
    backgroundColor: "#004537",
    color: "#FFFFFF",
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    flexShrink: 0,
  },
  sourceCardIcon: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    backgroundColor: "#F0F2EE",
    color: "#004537",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    flexShrink: 0,
  },
  sourceCardLabel: {
    fontSize: { xs: 12, sm: 13 },
    fontWeight: 600,
    color: "#0A0A0A",
    lineHeight: 1.2,
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  sourceCardBadge: {
    ml: "auto",
    display: "inline-flex",
    alignItems: "center",
    px: 0.75,
    height: 16,
    borderRadius: 999,
    backgroundColor: "#004537",
    color: "#FFFFFF",
    fontSize: 8,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    flexShrink: 0,
  },
  sourceContent: {
    mt: 2.5,
    p: 2,
    borderRadius: 3,
    border: `1.5px solid ${LIGHT_GREY}`,
    backgroundColor: "white",
  },
  checklistRecommendedBadge: {
    display: "inline-flex",
    alignItems: "center",
    px: 1,
    height: 20,
    ml: 1,
    borderRadius: 999,
    backgroundColor: "white",
    color: "black",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
  },
  writingSampleDivider: {
    my: 2,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    "&::before, &::after": {
      content: '""',
      flex: 1,
      borderTop: `1px solid ${LIGHT_GREY}`,
    },
  },
  writingSampleDividerLabel: {
    fontSize: 12,
    color: "#6B7168",
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    fontWeight: 600,
  },
  writingSampleAddButton: {
    mt: 1,
    alignSelf: "flex-start" as const,
  },
  writingSampleList: {
    mt: 2,
    display: "flex",
    flexDirection: "column" as const,
    gap: 1,
  },
  writingSampleRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    p: 1.5,
    borderRadius: 2,
    border: `1px solid ${LIGHT_GREY}`,
    backgroundColor: "#FFFFFF",
  },
  writingSampleContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: 0.5,
  },
  writingSampleLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7168",
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
  },
  writingSampleText: {
    flex: 1,
    fontSize: 13,
    color: "#0A0A0A",
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical" as const,
  },
  writingSampleRemoveButton: {
    minWidth: 0,
    p: 0.5,
    color: "#6B7168",
  },

  // ChipsWithOther
  chipsWrapper: { display: "flex", flexWrap: "wrap", gap: 1 },
  chip: (active: boolean) => ({
    px: 1.75,
    height: 36,
    borderRadius: 999,
    border: `1.5px solid ${active ? "#004537" : LIGHT_GREY}`,
    backgroundColor: active ? "#004537" : "white",
    color: active ? "white" : "#0A0A0A",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "inherit",
    transition: "all 0.15s ease",
    "&:hover": { borderColor: "#004537" },
  }),
  chipIconSmall: { fontSize: 14 },
  chipInputPill: {
    display: "inline-flex",
    alignItems: "center",
    height: 36,
    px: 1.75,
    borderRadius: 999,
    border: `1.5px solid #004537`,
    backgroundColor: "white",
  },
  chipInput: {
    outline: "none",
    border: "none",
    background: "transparent",
    width: 120,
    fontSize: 13,
    fontFamily: "inherit",
  },
  chipAddButton: {
    px: 1.75,
    height: 36,
    borderRadius: 999,
    border: `1.5px dashed ${LIGHT_GREY}`,
    backgroundColor: "transparent",
    color: "#6B7168",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "inherit",
  },
  chipPlusIcon: { fontSize: 12 },

  // Pitch length slider (3 stops)
  pitchLengthSliderWrapper: { px: 1, mt: 1 },
  pitchLengthSlider: {
    color: "#004537",
    "& .MuiSlider-rail": {
      opacity: 0.3,
      backgroundColor: LIGHT_GREY,
    },
    "& .MuiSlider-mark": {
      height: 10,
      width: 2,
      backgroundColor: "rgba(21,31,22,0.3)",
    },
    "& .MuiSlider-markActive": { backgroundColor: "#004537" },
    "& .MuiSlider-markLabel": {
      fontSize: 12,
      fontWeight: 500,
      mt: 0.5,
    },
  },
  pitchLengthDetail: {
    border: `1px solid ${LIGHT_GREY}`,
    borderRadius: 2,
    p: 1.5,
    mt: 2,
    mb: 3,
    display: "flex",
    flexDirection: "column",
    gap: 0.25,
  },
  pitchLengthDetailHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: 0.75,
  },
  pitchLengthDetailLabel: { fontWeight: 600, fontSize: 14 },
  pitchLengthDetailHint: { fontSize: 12, color: "text.secondary" },
  pitchLengthDetailRange: { fontSize: 12, color: "text.secondary" },

  // Step3: Generating / loading screen
  generatingContainer: (isMobile: boolean) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    px: isMobile ? 4 : 6,
  }),
  generatingSpinnerWrapper: (isMobile: boolean) => ({
    position: "relative",
    width: isMobile ? 80 : 96,
    height: isMobile ? 80 : 96,
    mb: isMobile ? 3 : 4,
  }),
  generatingSpinnerTrack: {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: "4px solid #E7EFEA",
  },
  generatingSpinnerProgress: {
    position: "absolute",
    inset: 0,
    color: "#0E3B2E",
  },
  generatingTitle: (isMobile: boolean) => ({
    fontSize: isMobile ? 22 : 32,
    fontWeight: 700,
    color: "#0A0A0A",
    mb: isMobile ? 0.5 : 1,
  }),
  generatingSubtitle: (isMobile: boolean) => ({
    fontSize: isMobile ? 13 : 15,
    color: "#6B7168",
    mb: isMobile ? 3 : 4,
  }),
  generatingStepsList: (isMobile: boolean) => ({
    width: "100%",
    maxWidth: isMobile ? 384 : 448,
    display: "flex",
    flexDirection: "column",
    gap: 1,
  }),
  generatingStepRow: (isMobile: boolean, active: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: isMobile ? 1 : 1.5,
    p: isMobile ? 1.5 : 2,
    borderRadius: 2,
    backgroundColor: active ? "#FFFFFF" : "transparent",
  }),
  generatingStepIconDone: (isMobile: boolean) => ({
    fontSize: isMobile ? 16 : 20,
    color: "#2E7A5B",
  }),
  generatingStepIconActive: (isMobile: boolean) => ({
    width: isMobile ? 16 : 20,
    height: isMobile ? 16 : 20,
    color: "#0E3B2E",
  }),
  generatingStepIconPending: (isMobile: boolean) => ({
    width: isMobile ? 16 : 20,
    height: isMobile ? 16 : 20,
    borderRadius: "50%",
    border: "2px solid #C9CDC3",
  }),
  generatingStepLabel: (isMobile: boolean, highlight: boolean) => ({
    fontSize: isMobile ? 13 : 15,
    fontWeight: highlight ? 600 : 400,
    color: highlight ? "#0A0A0A" : "#8C9188",
  }),
  // OneTweak
  oneTweakCard: {
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
    gap: 1.5,
    p: 2,
    mt: 2,
    borderRadius: 2,
    backgroundColor: "#FFF6E5",
    border: "1px solid #F0D9A0",
    textAlign: "left" as const,
    cursor: "pointer",
    fontFamily: "inherit",
    color: "#5C4A1A",
    transition: "opacity 0.15s ease",
    "&:hover": { opacity: 0.92 },
    "&:active": { opacity: 0.8 },
  },
  oneTweakIcon: {
    color: "#8A6A1F",
    fontSize: 18,
    flexShrink: 0,
    mt: "2px",
  },
  oneTweakContent: { flex: 1, minWidth: 0 },
  oneTweakReadMore: {
    display: "inline-block",
    mt: 1,
    fontSize: 13,
    fontWeight: 600,
    color: "#8A6A1F",
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    "&:hover": { color: "#6E5318" },
  },
};

export default styles;
